import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Checkbox, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { useCurrentUser } from '../lib/useCurrentUser';
import { displayRate, rateUnitLabel, storedRateUnit, RateUnit } from '../lib/millage';
import { formatDate } from '../lib/orgTimezone';

/**
 * JurisdictionAnnualInfoPanel — the bespoke Annual-Information surface on a
 * jurisdiction profile (Government data-model §2). Renders AND edits the
 * `jurisdiction_annual_info` row for a chosen tax year (defaulting to the org's
 * current system year):
 *
 *   - tax-year begin / end dates
 *   - tax type (Value Based [default] · Credit · Direct Assessment · Flat Tax)
 *   - assessment ratio (tax_ratio) + tax rate (tax_rate)
 *   - a millage flag (is_millage_rate) — the rate DISPLAY toggle re-expresses the
 *     stored rate per $100 vs per $1,000 (a pure decimal shift; §2.1)
 *   - a certified/confirmed checkbox (rate_is_confirmed) — the rate is provisional
 *     until confirmed (§2.3)
 *
 * On a CONFIRMED rate save the panel fires the existing single-point-of-change
 * cascade (POST /api/jurisdiction-annual-info/:id/cascade-rate) so downstream
 * tax_records + tax_bill_details recompute (§ rate-cascade).
 *
 * The `jurisdiction_annual_info` list/create/update flow through the generic-CRUD
 * resource (`/api/jurisdiction-annual-info`, org-scoped via its jurisdiction).
 */

export interface JurisdictionAnnualInfoPanelProps {
  jurisdictionId: string;
  canEdit: boolean;
}

interface AnnualInfoRow {
  id: string;
  jurisdiction_id: string;
  tax_year: number;
  tax_year_begins?: string | null;
  tax_year_ends?: string | null;
  tax_type?: string | null;
  tax_ratio?: number | null;
  tax_rate?: number | null;
  rate_is_confirmed?: boolean | number | null;
  is_millage_rate?: boolean | number | null;
  flat_tax_amount?: number | null;
  [k: string]: unknown;
}

const TAX_TYPE_OPTIONS = [
  { value: 'value_based', label: 'Value Based' },
  { value: 'credit', label: 'Credit' },
  { value: 'direct_assessment', label: 'Direct Assessment' },
  { value: 'flat_tax', label: 'Flat Tax' },
];

const labelStyle = {
  display: 'block',
  marginBottom: 'var(--sp-1)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--color-ink)',
} as const;

function truthy(v: unknown): boolean {
  return v === true || v === 1 || v === '1' || v === 'true';
}

/** Editable draft — every field a string so inputs are controlled. */
interface Draft {
  tax_year_begins: string;
  tax_year_ends: string;
  tax_type: string;
  tax_ratio: string;
  tax_rate: string;
  is_millage_rate: boolean;
  rate_is_confirmed: boolean;
  flat_tax_amount: string;
}

function draftFromRow(row: AnnualInfoRow | undefined): Draft {
  return {
    tax_year_begins: (row?.tax_year_begins as string) ?? '',
    tax_year_ends: (row?.tax_year_ends as string) ?? '',
    tax_type: (row?.tax_type as string) || 'value_based',
    tax_ratio: row?.tax_ratio != null ? String(row.tax_ratio) : '',
    tax_rate: row?.tax_rate != null ? String(row.tax_rate) : '',
    is_millage_rate: truthy(row?.is_millage_rate),
    rate_is_confirmed: truthy(row?.rate_is_confirmed),
    flat_tax_amount: row?.flat_tax_amount != null ? String(row.flat_tax_amount) : '',
  };
}

export function JurisdictionAnnualInfoPanel({ jurisdictionId, canEdit }: JurisdictionAnnualInfoPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const orgYear = typeof currentUser?.current_system_year === 'number'
    ? (currentUser.current_system_year as number)
    : null;
  // `year` is null until the operator picks one; effectiveYear tracks the org's
  // current system year reactively (falling back to the calendar year).
  const [year, setYear] = useState<number | null>(null);
  const effectiveYear = year ?? orgYear ?? new Date().getFullYear();

  const listQuery = useQuery<AnnualInfoRow[]>({
    queryKey: ['jurisdiction-annual-info', jurisdictionId],
    queryFn: ({ signal }) =>
      api.get<AnnualInfoRow[]>(`/api/jurisdiction-annual-info?jurisdiction_id=${jurisdictionId}`, signal),
    enabled: !!jurisdictionId,
  });

  const rows = listQuery.data ?? [];
  const row = rows.find((r) => Number(r.tax_year) === effectiveYear);

  // Year options: a window around the effective year, unioned with every year
  // that already has a row, so switching to an existing year is always possible.
  const yearOptions = useMemo(() => {
    const set = new Set<number>();
    for (let y = effectiveYear - 2; y <= effectiveYear + 3; y += 1) set.add(y);
    for (const r of rows) set.add(Number(r.tax_year));
    return [...set].sort((a, b) => b - a).map((y) => ({ value: String(y), label: String(y) }));
  }, [rows, effectiveYear]);

  // Display-only rate unit toggle (§2.1) — defaults to the stored millage flag.
  const [displayUnit, setDisplayUnit] = useState<RateUnit | null>(null);
  const effectiveUnit: RateUnit = displayUnit ?? storedRateUnit(row?.is_millage_rate);

  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFromRow(undefined));

  function openEditor() {
    setDraft(draftFromRow(row));
    setModalOpen(true);
  }

  const save = useMutation<{ cascaded: boolean }, Error, void>({
    mutationFn: async () => {
      // Build the persisted payload. Empty strings → null (an unset optional
      // field), matching the generic create/edit modal's coercion.
      const body: Record<string, unknown> = {
        tax_year_begins: draft.tax_year_begins || null,
        tax_year_ends: draft.tax_year_ends || null,
        tax_type: draft.tax_type || null,
        tax_ratio: draft.tax_ratio === '' ? null : Number(draft.tax_ratio),
        tax_rate: draft.tax_rate === '' ? null : Number(draft.tax_rate),
        is_millage_rate: draft.is_millage_rate,
        rate_is_confirmed: draft.rate_is_confirmed,
        flat_tax_amount: draft.flat_tax_amount === '' ? null : Number(draft.flat_tax_amount),
      };

      let saved: AnnualInfoRow;
      if (row) {
        saved = await api.put<AnnualInfoRow>(`/api/jurisdiction-annual-info/${row.id}`, body);
      } else {
        saved = await api.post<AnnualInfoRow>('/api/jurisdiction-annual-info', {
          ...body,
          jurisdiction_id: jurisdictionId,
          tax_year: effectiveYear,
        });
      }

      // §rate-cascade: a CONFIRMED rate edit re-runs the single-point-of-change
      // cascade so downstream parcels/bills recompute. Idempotent — a no-op edit
      // reports 0 updates. Provisional (unconfirmed) rates do NOT cascade (§2.3).
      const savedId = saved?.id ?? row?.id;
      if (savedId && draft.rate_is_confirmed && body.tax_rate != null) {
        await api.post(`/api/jurisdiction-annual-info/${savedId}/cascade-rate`, {});
        return { cascaded: true };
      }
      return { cascaded: false };
    },
    onSuccess: ({ cascaded }) => {
      toast({
        variant: 'success',
        message: cascaded
          ? `Annual info saved and rate cascaded for ${effectiveYear}.`
          : `Annual info saved for ${effectiveYear}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['jurisdiction-annual-info', jurisdictionId] });
      if (cascaded) {
        // The cascade touches tax_records + tax_bill_details across parcels.
        queryClient.invalidateQueries({ queryKey: ['parcels-list'] });
        queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      }
      setModalOpen(false);
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  if (listQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading annual information…</p>;
  }
  if (listQuery.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <strong>Failed to load annual information:</strong> {(listQuery.error as Error).message}
      </div>
    );
  }

  const shownRate = displayRate(row?.tax_rate as number | null | undefined, row?.is_millage_rate, effectiveUnit);

  return (
    <div>
      {/* Year selector + edit control */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-4)',
        flexWrap: 'wrap',
      }}>
        <label style={{ display: 'block', minWidth: 160 }}>
          <span style={labelStyle}>Tax year</span>
          <Select
            preserveOrder
            value={String(effectiveYear)}
            onChange={(e) => setYear(Number(e.target.value))}
            options={yearOptions}
          />
        </label>
        {canEdit && (
          <Button variant="primary" size="sm" onClick={openEditor}>
            {row ? 'Edit annual info' : `Add annual info for ${effectiveYear}`}
          </Button>
        )}
      </div>

      {!row ? (
        <div style={{
          padding: 'var(--sp-7)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          <p style={{ margin: 0 }}>No annual information recorded for {effectiveYear}.</p>
        </div>
      ) : (
        <div style={{
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--sp-5)',
        }}>
          {/* Confirmed / provisional status (§2.3) */}
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            {truthy(row.rate_is_confirmed) ? (
              <Badge variant="success" dot>Rate confirmed</Badge>
            ) : (
              <Badge variant="warning" dot>Provisional rate</Badge>
            )}
          </div>

          <dl style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px, max-content) 1fr',
            rowGap: 'var(--sp-3)',
            columnGap: 'var(--sp-5)',
            margin: 0,
          }}>
            <Row label="Tax type" value={taxTypeLabel(row.tax_type)} />
            <Row label="Tax year begins" value={row.tax_year_begins ? formatDate(row.tax_year_begins) : '—'} />
            <Row label="Tax year ends" value={row.tax_year_ends ? formatDate(row.tax_year_ends) : '—'} />
            <Row label="Assessment ratio" value={row.tax_ratio != null ? String(row.tax_ratio) : '—'} />

            {/* Rate + display-unit toggle (§2.1) */}
            <dt style={dtStyle}>Tax rate</dt>
            <dd style={ddStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'var(--fw-medium)' }}>
                  {shownRate != null ? `${shownRate} ${rateUnitLabel(effectiveUnit)}` : '—'}
                </span>
                <div role="group" aria-label="Rate display unit" style={{ display: 'inline-flex', gap: 'var(--sp-1)' }}>
                  <UnitButton
                    active={effectiveUnit === 'per100'}
                    onClick={() => setDisplayUnit('per100')}
                  >per $100</UnitButton>
                  <UnitButton
                    active={effectiveUnit === 'per1000'}
                    onClick={() => setDisplayUnit('per1000')}
                  >per $1,000</UnitButton>
                </div>
              </div>
            </dd>

            {taxTypeLabel(row.tax_type) === 'Flat Tax' && (
              <Row label="Flat tax amount" value={row.flat_tax_amount != null ? String(row.flat_tax_amount) : '—'} />
            )}
          </dl>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => { if (!save.isPending) setModalOpen(false); }}
        title={`Annual information — ${effectiveYear}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        }
      >
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); if (!save.isPending) save.mutate(); }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
            <label>
              <span style={labelStyle}>Tax year begins</span>
              <Input
                type="date"
                value={draft.tax_year_begins}
                onChange={(e) => setDraft((d) => ({ ...d, tax_year_begins: e.target.value }))}
              />
            </label>
            <label>
              <span style={labelStyle}>Tax year ends</span>
              <Input
                type="date"
                value={draft.tax_year_ends}
                onChange={(e) => setDraft((d) => ({ ...d, tax_year_ends: e.target.value }))}
              />
            </label>
            <label>
              <span style={labelStyle}>Tax type</span>
              <Select
                value={draft.tax_type}
                onChange={(e) => setDraft((d) => ({ ...d, tax_type: e.target.value }))}
                options={TAX_TYPE_OPTIONS}
              />
            </label>
            <label>
              <span style={labelStyle}>Assessment ratio</span>
              <Input
                type="number"
                step="0.0001"
                value={draft.tax_ratio}
                onChange={(e) => setDraft((d) => ({ ...d, tax_ratio: e.target.value }))}
                placeholder="e.g. 1.0"
              />
            </label>
            <label>
              <span style={labelStyle}>Tax rate</span>
              <Input
                type="number"
                step="0.0001"
                value={draft.tax_rate}
                onChange={(e) => setDraft((d) => ({ ...d, tax_rate: e.target.value }))}
                placeholder="e.g. 2.5"
              />
            </label>
            {draft.tax_type === 'flat_tax' && (
              <label>
                <span style={labelStyle}>Flat tax amount</span>
                <Input
                  type="number"
                  step="0.01"
                  value={draft.flat_tax_amount}
                  onChange={(e) => setDraft((d) => ({ ...d, flat_tax_amount: e.target.value }))}
                />
              </label>
            )}
          </div>

          <div style={{ marginTop: 'var(--sp-4)', display: 'grid', gap: 'var(--sp-3)' }}>
            <Checkbox
              label="Millage rate (per $1,000)"
              description="Tax is assessed per $1,000 of value rather than per $100."
              checked={draft.is_millage_rate}
              onChange={(e) => setDraft((d) => ({ ...d, is_millage_rate: e.target.checked }))}
            />
            <Checkbox
              label="Rate confirmed / certified"
              description="Leave unchecked while the rate is provisional. Confirming re-runs the downstream tax cascade."
              checked={draft.rate_is_confirmed}
              onChange={(e) => setDraft((d) => ({ ...d, rate_is_confirmed: e.target.checked }))}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}

const dtStyle = { color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' } as const;
const ddStyle = { margin: 0, color: 'var(--color-ink)', fontSize: 'var(--fs-md)' } as const;

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={dtStyle}>{label}</dt>
      <dd style={ddStyle}>{value}</dd>
    </>
  );
}

function UnitButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        fontFamily: 'inherit',
        fontSize: 'var(--fs-sm)',
        padding: '4px 10px',
        borderRadius: 'var(--r-md)',
        border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--color-border)'),
        background: active ? 'var(--brand-primary-bg)' : 'var(--color-card)',
        color: active ? 'var(--brand-primary)' : 'var(--color-ink-3)',
        cursor: 'pointer',
        fontWeight: active ? 'var(--fw-medium)' : 'var(--fw-normal)',
      }}
    >
      {children}
    </button>
  );
}

function taxTypeLabel(value: unknown): string {
  const opt = TAX_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : (typeof value === 'string' && value ? value : '—');
}
